// Mail — what EmmA read, and what she did with it.
//
// Jacky's brief, and the reason this page is shaped the way it is:
//   "her job is categorize them, help me clean it, and extract information from
//    them that relate to a job … not replying but take note and display it to me.
//    if I need assist writing reply I will do it through chat with emma"
//   "many mailbox, no time to look at all, how to make it easy for me"
//
// SO THE PAGE OPENS ON A NUMBER, NOT AN INBOX. Three buckets, in the order his
// attention is worth spending:
//   1. Needs you        — money, dates, commitments, or a customer waiting
//   2. Waiting on a rule — she tagged it, nothing may move until he arms the rule
//   3. Filed             — a receipt line he can expand, never a list to read
//
// NOTHING HERE SENDS. Replying happens in chat, when he asks. The only write on
// this page is arming a cleanup rule, which is his own setting.
import { sydneyDayOf, sydneyToday } from "@artemis-group/aiw-contracts";
import {useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {Page, Panel} from '../shell/Page';
import {cn} from '@aiw/ui';
import {useT} from '../lib/i18n';
import {armRule, loadMail, setHandled, setRule, type MailItem} from '../lib/mail';

/** A tag as a reader sees it. Labels live in the message catalog, but the
 *  vocabulary is DATA — she coins tags — so an unknown one falls back to its id
 *  with the underscores removed rather than rendering a missing-key id. */
const TAG_KEYS = new Set([
  'supplier_order_confirmation','supplier_invoice','supplier_quote','supplier_product_update',
  'customer_job_lead','action_required_admin','marketing_newsletter','payment_confirmation','needs_attention',
]);
const tagLabel = (tag: string, t: (k: never, v?: Record<string, string>) => string): string =>
  TAG_KEYS.has(tag) ? (t as unknown as (k: string) => string)(`mail.tag.${tag}`) : tagWords(tag);

const when = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  // "Today" is the Sydney day, not the device's.
  const same = sydneyDayOf(iso) === sydneyToday();
  return same ? d.toLocaleTimeString('en-AU', {hour: '2-digit', minute: '2-digit', timeZone: 'Australia/Sydney'}) : d.toLocaleDateString('en-AU', {day: '2-digit', month: 'short', timeZone: 'Australia/Sydney'});
};

const tagWords = (tag: string) => tag.replace(/_/g, ' ');

/** Where a tag would sensibly file, as a starting point he can edit. Derived from
 *  the tag rather than guessed from the mail, so it is predictable. */
export function defaultFolder(item: MailItem): string {
  const tag = item.rule_tag ?? item.tags[0] ?? '';
  if (tag.startsWith('supplier_')) {
    const who = (item.facts.find((f) => f.label === 'Supplier')?.value ?? '').trim();
    return who ? `Suppliers/${who}` : 'Suppliers';
  }
  if (tag.startsWith('customer') || tag.startsWith('quote')) return 'Customers';
  if (tag.startsWith('payment') || tag === 'supplier_invoice') return 'Accounts';
  return 'Filed';
}

/** Run an action, hold the button, report what went wrong. One place, so every
 *  button on the page fails the same visible way instead of silently. */
async function act(run: () => Promise<unknown>, onDone?: () => void, setBusy?: (b: boolean) => void) {
  setBusy?.(true);
  try {
    await run();
    onDone?.();
  } finally {
    setBusy?.(false);
  }
}

function Card({item, onArm, onDone, onDraft, armable}: {item: MailItem; onArm: (tag: string, folder: string) => Promise<void>; onDone: (id: string, handled: boolean) => Promise<void>; onDraft: (item: MailItem) => void; armable: boolean}) {
  const {t} = useT();
  const [open, setOpen] = useState(false);
  const [asking, setAsking] = useState(false);
  const [copiedHere, setCopiedHere] = useState(false);
  const [folder, setFolder] = useState(defaultFolder(item));
  const [busy, setBusy] = useState(false);
  const waiting = item.rule_state === 'waiting' && armable;
  const filed = item.rule_state === 'armed';
  return (
    <article className={cn('rounded-lg border bg-card', filed ? 'border-l-2 border-l-success' : item.needs_attention ? 'border-l-2 border-l-warning' : waiting ? 'border-l-2 border-l-primary' : '')}>
      <button type="button" onClick={() => setOpen(!open)} className="w-full px-3 py-2.5 text-left hover:bg-muted/30">
        <div className="flex flex-wrap items-center gap-1.5 text-micro text-muted-foreground">
          <span className="rounded bg-muted px-1.5 font-mono text-primary">{item.mailbox}@</span>
          <span className="truncate">{item.from_name || item.from}</span>
          {item.tags.map((tag) => <span key={tag} className="rounded-full border px-2">{tagLabel(tag, t)}</span>)}
          <span className="ml-auto shrink-0 tabular-nums">{when(item.received_at)}</span>
        </div>
        <div className="mt-1 text-sm font-semibold">{item.subject}</div>
        <div className="text-micro text-muted-foreground">{item.summary}</div>
        {(item.job_ref || item.facts.length > 0) && (
          <div className="mt-1.5 flex flex-wrap gap-1.5 text-micro">
            {item.job_ref && <span className="rounded border border-primary/40 bg-primary/10 px-1.5 text-primary">{t('mail.job')} <b>{item.job_ref}</b></span>}
            {item.facts.map((f) => <span key={f.label} className="rounded border px-1.5 text-muted-foreground">{f.label} <b className="text-foreground tabular-nums">{f.value}</b></span>)}
          </div>
        )}
      </button>
      <div className="border-t border-dashed px-3 py-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-micro text-muted-foreground">{item.handled ? t('mail.handled') : filed ? t('mail.filedByRule') : waiting ? t('mail.needsRule') : item.needs_attention ? t('mail.needsYourCall') : t('mail.nothingToDo')}</span>
          {item.folder && !asking && <span className="rounded border bg-background px-1.5 font-mono text-micro">{item.folder}</span>}
          <div className="ml-auto flex gap-1.5">
            {item.handled ? (
              <button type="button" disabled={busy} onClick={() => void act(() => onDone(item.id, false), undefined, setBusy)} className="rounded border px-2.5 py-1 text-micro hover:border-foreground/40">
                {t('mail.putBack')}
              </button>
            ) : (
              <>
                {item.rule_tag && <button type="button" disabled={busy} onClick={() => setAsking(true)} className="rounded bg-primary px-2.5 py-1 text-micro font-medium text-primary-foreground hover:opacity-90">{t('mail.fileIt')}</button>}
                <button type="button" disabled={busy} onClick={() => { void onDraft(item); setCopiedHere(true); setTimeout(() => setCopiedHere(false), 4000); }} className="rounded border px-2.5 py-1 text-micro hover:border-foreground/40">{copiedHere ? t('mail.copied') : t('mail.askDraft')}</button>
                <button type="button" disabled={busy} onClick={() => void act(() => onDone(item.id, true), undefined, setBusy)} className="rounded border px-2.5 py-1 text-micro hover:border-foreground/40">{t('mail.done')}</button>
              </>
            )}
          </div>
        </div>
        {/* A tag with no rule still gets a way to make one — otherwise the common
            case is a card with nothing to click, which is how this page failed him
            the first time it shipped. */}
        {asking && item.rule_tag && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="text-micro text-muted-foreground">{t('mail.fileWhere')}</span>
            <input value={folder} onChange={(e) => setFolder(e.target.value)} placeholder="Suppliers/Paco Floors" className="min-w-[190px] flex-1 rounded border bg-background px-2 py-1 font-mono text-micro" />
            <button type="button" disabled={busy || !folder.trim()} onClick={() => void act(() => onArm(item.rule_tag!, folder), () => setAsking(false), setBusy)} className="rounded bg-primary px-2.5 py-1 text-micro font-medium text-primary-foreground disabled:opacity-50">
              {t('mail.armAndFile')}
            </button>
            <button type="button" onClick={() => setAsking(false)} className="text-micro text-muted-foreground hover:underline">{t('common.close')}</button>
          </div>
        )}
      </div>
      {open && (
        <div className="border-t px-3 py-2 text-micro text-muted-foreground">
          <div className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1">
            <span>{t('mail.why')}</span><span>{item.needs_attention ? t('mail.whyNeeds') : filed ? t('mail.whyFiled') : t('mail.whyWaiting')}</span>
            <span>{t('mail.readBy')}</span><span>{item.read_via ?? t('mail.readByUnknown')}</span>
          </div>
        </div>
      )}
    </article>
  );
}

export default function Mail() {
  const {t} = useT();
  const [box, setBox] = useState('all');
  // WHAT FITS. A queue of 100 unread would make the page the wall it exists to
  // replace, so a bucket shows the newest few and offers the rest behind a count.
  const [showAll, setShowAll] = useState(false);
  const [showFiled, setShowFiled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const q = useQuery({queryKey: ['mail', box], queryFn: () => loadMail({mailbox: box}), refetchInterval: 60_000});

  if (q.isPending) return <Page title={t('mail.title')}><p role="status" className="text-sm">{t('mail.loading')}</p></Page>;
  if (q.isError) return <Page title={t('mail.title')}><p className="text-sm text-danger">{t('mail.unavailable', {message: (q.error as Error).message})}</p></Page>;

  const board = q.data;
  const PAGE = 8;
  const needsAll = board.items.filter((i) => i.needs_attention);
  const waitingAll = board.items.filter((i) => !i.needs_attention && i.rule_state !== 'armed');
  const filedAll = board.items.filter((i) => !i.needs_attention && i.rule_state === 'armed');
  const needs = showAll ? needsAll : needsAll.slice(0, PAGE);
  const waiting = showAll ? waitingAll : waitingAll.slice(0, PAGE);
  const filed = showAll ? filedAll : filedAll.slice(0, PAGE);
  const hidden = needsAll.length + waitingAll.length + filedAll.length - (needs.length + waiting.length + filed.length);
  const armedTags = new Set(board.rules.filter((r) => r.enabled && r.action === 'move').map((r) => r.tag));

  const arm = async (tag: string, folder: string) => {
    setError(null);
    try {
      await armRule(tag, folder);
      await q.refetch();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const done = async (id: string, handled: boolean) => {
    setError(null);
    try {
      await setHandled(id, handled);
      await q.refetch();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  // A draft is CHAT's job — his own instruction was that replies happen there.
  // So this hands her the mail and the context and opens the drawer; it does not
  // compose anything here.
  // REPLIES ARE CHAT'S JOB — his own instruction — and the drawer's composer is
  // not reachable from a page yet. So rather than half-wire it, this puts the
  // context on the clipboard and says so. A button that looks like it opened a
  // conversation and did nothing would be worse than one honest step.
  // NOTE: no hook may be declared below this point. The two early returns above
  // (loading, error) mean a `useState` here runs on the second render but not the
  // first — React error #310, and a blank page in production. The "copied ✓" label
  // belongs to the card, which already owns that state.
  const draft = async (item: MailItem) => {
    const line = `${item.subject} — ${item.from_name ?? item.from} (${item.mailbox}@)${item.job_ref ? ` · job ${item.job_ref}` : ''}`;
    try {
      await navigator.clipboard.writeText(`${t('mail.draftLead')}\n${line}\n\n${item.summary}`);
    } catch {
      setError(t('mail.copyFailed'));
    }
  };

  return (
    <Page kicker={t('mail.kicker')} title={t('mail.title')} lede={t('mail.lede')}>
      {error && <p className="mb-3 rounded-lg border border-l-4 border-l-danger bg-danger/10 px-3 py-2 text-micro">{error}</p>}
      {board.vocabulary_unavailable && <p className="mb-3 rounded-lg border border-l-4 border-l-warning bg-warning/10 px-3 py-2 text-micro">{t('mail.vocabularyUnavailable')}</p>}

      {/* The one number that decides whether he has to stop. */}
      <div className={cn('mb-3 flex items-center gap-4 rounded-lg border border-l-4 bg-card px-4 py-2.5', needs.length ? 'border-l-warning' : 'border-l-success')}>
        <div className={cn('font-display text-3xl font-bold tabular-nums tracking-tight', needs.length ? 'text-warning' : 'text-success')}>{needs.length}</div>
        <div className="text-micro text-muted-foreground">
          {needs.length ? t('mail.verdictNeed', {total: String(board.counts.all)}) : t('mail.verdictClear')}
        </div>
      </div>

      {/* Five trays as filters, not destinations. */}
      <div className="mb-3 flex flex-wrap gap-1.5">
        {[{mailbox: 'all', count: board.counts.all, needs: board.counts.needs}, ...board.boxes].map((b) => (
          <button key={b.mailbox} type="button" onClick={() => setBox(b.mailbox)} className={cn('rounded-full border px-3 py-1 text-micro', box === b.mailbox ? 'border-primary bg-primary/10 font-medium text-primary' : 'text-muted-foreground hover:border-foreground/30')}>
            {b.mailbox === 'all' ? t('mail.allBoxes') : `${b.mailbox}@`} <b className="tabular-nums text-foreground">{b.count}</b>
          </button>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="grid gap-4 md:grid-cols-2">
          <section>
            <h2 className="mb-2 flex items-center gap-2 font-display text-sm font-semibold">
              {t('mail.needsYou')} <span className="rounded-full bg-muted px-2 text-micro tabular-nums">{needs.length}</span>
            </h2>
            <div className="grid gap-2">{needs.map((i) => <Card key={i.id} item={i} onArm={arm} onDone={done} onDraft={draft} armable />)}</div>
            {needs.length === 0 && <p className="text-micro text-muted-foreground">{t('mail.noneNeed')}</p>}
          </section>

          <section>
            <h2 className="mb-2 flex items-center gap-2 font-display text-sm font-semibold">
              {t('mail.waitingRule')} <span className="rounded-full bg-muted px-2 text-micro tabular-nums">{waiting.length}</span>
            </h2>
            <div className="grid gap-2">{waiting.map((i) => <Card key={i.id} item={i} onArm={arm} onDone={done} onDraft={draft} armable={!i.rule_tag || !armedTags.has(i.rule_tag)} />)}</div>
            {waiting.length === 0 && <p className="text-micro text-muted-foreground">{t('mail.noneWaiting')}</p>}
          </section>
        </div>

        <aside className="grid content-start gap-3">
          <Panel title={t('mail.rules')} meta={t('mail.rulesMeta')}>
            <div className="grid gap-1 px-4 py-3">
              {board.rules.map((r) => (
                <div key={r.tag} className="flex items-center gap-2 border-b py-1.5 last:border-0">
                  <button type="button" aria-label={`${r.tag} ${r.enabled ? 'on' : 'off'}`} onClick={() => void arm2(r.tag, !r.enabled, q.refetch, setError)} className={cn('relative h-5 w-9 shrink-0 rounded-full border transition-colors', r.enabled ? 'border-success bg-success' : 'bg-muted')}>
                    <span className={cn('absolute top-0.5 h-3.5 w-3.5 rounded-full bg-background shadow transition-all', r.enabled ? 'left-4.5' : 'left-0.5')} />
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="text-micro font-medium">{r.label}</div>
                    <div className="truncate font-mono text-micro text-muted-foreground">→ {r.folder ?? t('mail.noFolder')}</div>
                  </div>
                </div>
              ))}
              {board.rules.length === 0 && <p className="text-micro text-muted-foreground">{t('mail.noRules')}</p>}
            </div>
          </Panel>

          <Panel title={t('mail.vocabulary')} meta={t('mail.tagCount', {n: String(board.tags.length)})}>
            <div className="grid gap-1 px-4 py-3">
              {board.tags.map((tag) => (
                <div key={tag.id} className="flex items-center justify-between gap-2 text-micro">
                  <span>{tag.label}</span><span className="tabular-nums text-muted-foreground">{tag.uses}</span>
                </div>
              ))}
              {board.proposed.length > 0 && (
                <p className="mt-1 border-t pt-2 text-micro text-muted-foreground">{t('mail.proposed', {n: String(board.proposed.length), tag: board.proposed[0]!.label})}</p>
              )}
            </div>
          </Panel>
        </aside>
      </div>

      <section className="mt-3">
        <button type="button" onClick={() => setShowFiled(!showFiled)} className="flex w-full items-center gap-3 rounded-lg border border-l-2 border-l-success bg-card px-4 py-2.5 text-left">
          <span className="text-success">✓</span>
          <span className="text-sm">{t('mail.filedLine', {n: String(filed.length)})}</span>
          <span className="ml-auto text-micro text-muted-foreground">{t('mail.filedWhy')}</span>
        </button>
        {showFiled && <div className="mt-2 grid gap-2">{filed.map((i) => <Card key={i.id} item={i} onArm={arm} onDone={done} onDraft={draft} armable={false} />)}</div>}
      </section>

      {hidden > 0 && (
        <button type="button" onClick={() => setShowAll(true)} className="mt-3 w-full rounded-lg border py-2 text-micro text-muted-foreground hover:border-foreground/30">
          {t('mail.showMore', {n: String(hidden)})}
        </button>
      )}
    </Page>
  );
}

async function arm2(tag: string, enabled: boolean, refetch: () => Promise<unknown>, setError: (m: string | null) => void) {
  setError(null);
  try {
    await setRule(tag, enabled);
    await refetch();
  } catch (e) {
    setError((e as Error).message);
  }
}
